import { StripeError } from '@stripe/stripe-js'
import { APP_NAME } from 'app-config'

import { IRequestOptions, IResponseResult } from 'models/api.model'
import {
  IBaseSubscriptionInfoRaw,
  IInAppPurchaseResponseRaw,
  IPrimerClientSessionResponseRaw,
  IPrimerPaymentResponseRaw,
  IPurchaseRetrySubscriptionResponseRaw,
  ISubscriptionInfoRaw,
  ISubscriptionResponseRaw,
} from 'models/payment.model'

import { ApiService } from 'services/api.service'

import { Cohort, RequestMethod } from 'root-constants'
import { TAppName } from 'root-types'

export class PaymentApi {
  private readonly baseUrl = 'v2/purchases'
  private readonly appName: TAppName = APP_NAME
  private api: ApiService

  constructor(api: ApiService) {
    this.api = api
  }

  createSubscription({
    uuid,
    planId,
    trialPeriodDays,
    promoCode,
    cohort,
    paymentMethodId,
    upiVpa,
  }: {
    uuid: string
    planId: number
    promoCode?: string
    trialPeriodDays: number
    cohort: string
    paymentMethodId?: string
    upiVpa?: string
  }): Promise<IResponseResult<ISubscriptionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
        promo_code: promoCode,
        trial_duration: trialPeriodDays,
        cohort,
        payment_method_id: paymentMethodId,
        upi_vpa: upiVpa,
      },
    }

    return this.api.makeRequest<ISubscriptionResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${this.appName}`,
      options,
    )
  }

  retryPayment({
    uuid,
    stripeError,
  }: {
    uuid: string
    stripeError: StripeError
  }): Promise<IResponseResult<IPurchaseRetrySubscriptionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_error: stripeError,
      },
    }
    return this.api.makeRequest<IPurchaseRetrySubscriptionResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${this.appName}/retry`,
      options,
    )
  }

  createPaypalSubscription({
    uuid,
    paypalPlanId,
    cohort,
  }: {
    uuid: string
    paypalPlanId: string | null
    cohort: Cohort
  }): Promise<IResponseResult<never>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        paypal_plan_id: paypalPlanId,
        cohort,
      },
    }
    return this.api.makeRequest<IPurchaseRetrySubscriptionResponseRaw>(
      `${this.baseUrl}/paypal/${uuid}/${this.appName}`,
      options,
    )
  }

  makeInappPurchase({
    uuid,
    planId,
    productName,
  }: {
    uuid: string
    planId: number
    productName?: string
  }): Promise<IResponseResult<IInAppPurchaseResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
        product_name: productName,
      },
    }

    return this.api.makeRequest<IInAppPurchaseResponseRaw>(
      `${this.baseUrl}/${uuid}/${this.appName}/inapp`,
      options,
    )
  }

  makeUpsell({
    uuid,
    planId,
    productId,
    promoCode,
  }: {
    uuid: string
    planId: number
    productId: string
    promoCode?: string
  }): Promise<IResponseResult<ISubscriptionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        plan_id: planId,
        description: productId,
        promo_code: promoCode,
      },
    }

    return this.api.makeRequest<ISubscriptionResponseRaw>(
      `${this.baseUrl}/stripe/${uuid}/${this.appName}/upsell`,
      options,
    )
  }

  makePrimerUpsell({
    uuid,
    planId,
    productId,
    promoCode,
  }: {
    uuid: string
    planId: number
    productId: string
    promoCode?: string
  }): Promise<IResponseResult<IBaseSubscriptionInfoRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        description: productId,
        promo_code: promoCode,
        plan_id: planId,
      },
    }

    return this.api.makeRequest<IBaseSubscriptionInfoRaw>(
      `${this.baseUrl}/${uuid}/${this.appName}/upsell`,
      options,
    )
  }

  getPrimerClientSessionToken({
    uuid,
    cohort,
    planId,
    trialPeriodDays,
  }: {
    uuid: string
    cohort: string
    trialPeriodDays?: number
    planId: number
  }): Promise<IResponseResult<IPrimerClientSessionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        cohort,
        plan_id: planId,
        trial_duration: trialPeriodDays,
      },
    }

    return this.api.makeRequest<IPrimerClientSessionResponseRaw>(
      `${this.baseUrl}/primer/manual/${uuid}/${this.appName}/token`,
      options,
    )
  }

  updatePrimerClientSession({
    uuid,
    paymentId,
    clientToken,
  }: {
    uuid: string
    paymentId?: string
    clientToken: string
  }): Promise<IResponseResult<IPrimerClientSessionResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.PATCH,
      body: {
        client_token: clientToken,
        ...(paymentId && { payment_id: paymentId }),
      },
    }
    return this.api.makeRequest<IPrimerClientSessionResponseRaw>(
      `${this.baseUrl}/primer/manual/${uuid}/${this.appName}/token`,
      options,
    )
  }

  resumePrimerSubscription({
    uuid,
    paymentId,
    resumeToken,
  }: {
    uuid: string
    paymentId: string
    resumeToken: string
  }): Promise<IResponseResult<IPrimerPaymentResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_id: paymentId,
        resume_token: resumeToken,
      },
    }
    return this.api.makeRequest<never>(
      `${this.baseUrl}/primer/manual/${uuid}/${this.appName}/payment/resume`,
      options,
    )
  }

  createPrimerSubscription({
    uuid,
    token,
  }: {
    uuid: string
    token: string
  }): Promise<IResponseResult<IPrimerPaymentResponseRaw>> {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_method_token: token,
      },
    }
    return this.api.makeRequest<never>(
      `${this.baseUrl}/primer/manual/${uuid}/${this.appName}/payment`,
      options,
    )
  }

  confirmPrimerSubscription({
    uuid,
    paymentId,
  }: {
    uuid: string
    paymentId: string
  }): Promise<
    IResponseResult<{
      status: boolean
      purchase: ISubscriptionInfoRaw
      error?: boolean
    }>
  > {
    const options: IRequestOptions = {
      method: RequestMethod.POST,
      body: {
        payment_id: paymentId,
      },
    }
    return this.api.makeRequest<never>(
      `${this.baseUrl}/primer/manual/${uuid}/${this.appName}/confirm`,
      options,
    )
  }
}
